import { memo, useMemo } from 'react';
import SearchInput from './SearchInput.jsx';
import FormSelect from './FormSelect.jsx';
import FormMultiSelect from './FormMultiSelect.jsx';
import Button from './Button.jsx';
import { Filter } from 'lucide-react';

const defaultSelectWrapperClassName = 'min-w-[9rem] shrink-0';
const defaultSearchWidthClassName = 'min-w-[12rem] max-w-xl flex-1';
const defaultSearchInputClassName =
  'h-[34px] [&_input]:h-full [&_input]:min-h-0 [&_input]:py-0 [&_input]:leading-none';
const defaultFiltersButtonClassName =
  'shrink-0 gap-1.5 px-3 font-medium hover:border-accent/50 focus-visible:border-accent/50 focus-visible:ring-accent/50';
const defaultTriggerClassName =
  '!h-8 !min-h-8 !max-h-8 shrink-0 max-w-[16rem] min-w-[9rem] px-2.5 !py-0 text-xs leading-none hover:border-accent/50 focus:border-accent/50 focus:ring-1 focus:ring-accent/50';
const defaultListboxClassName =
  'absolute left-0 top-full z-[100] mt-1 w-max min-w-[max(100%,16rem)] max-w-[min(28rem,calc(100vw-2rem))] max-h-[min(24rem,55vh)] overflow-y-auto rounded-lg border border-edge bg-surface py-1 shadow-lg';
const contentJustifyMap = {
  start: 'justify-start',
  between: 'justify-between',
  end: 'justify-end',
};

const DefaultFiltersIcon = (props) => <Filter {...props} size={14} strokeWidth={2} />;

/**
 * Reusable toolbar row: SearchInput + configurable select filters + trailing actions.
 *
 * Filters are driven by config objects so host apps can map any domain shape.
 *
 * @param {object} props
 * @param {object} [props.searchInputProps] - Passed directly to SearchInput.
 * @param {Array<{
 *   id: string,
 *   name?: string,
 *   title?: string,
 *   placeholder?: string,
 *   value: string[]|string,
 *   onChange: (next: string[]|string) => void,
 *   options: { value: string, label: string }[],
 *   multiple?: boolean,
 *   showradioButton?: boolean,
 *   selectionIndicator?: 'auto'|'checkbox'|'radio',
 *   renderValue?: (selected: { value: string; label: string }[]) => string,
 *   align?: 'start'|'end',
 *   triggerClassName?: string,
 *   listboxClassName?: string,
 *   wrapperClassName?: string
 * }>} [props.filters=[]]
 * @param {import('react').ReactNode} [props.actions] - Right-side custom actions (buttons, toggles, etc).
 * @param {'start'|'between'|'end'} [props.contentJustify='between'] - Main distribution between search area and filters/actions.
 * @param {string} [props.searchWidthClassName] - Tailwind classes to control SearchInput width (e.g. `w-full`, `max-w-md`, `basis-80`).
 * @param {boolean} [props.showFiltersAction=false] - Render a default "Filters" button at right side.
 * @param {() => void} [props.onFiltersClick] - Click callback for the default filters button.
 * @param {string} [props.filtersActionLabel='Filters'] - Label text for the default filters button.
 * @param {import('react').ElementType|null} [props.filtersActionLeftIcon] - Optional left icon for default filters button.
 * @param {string} [props.className] - Root wrapper classes.
 * @param {string} [props.rowClassName] - Row layout classes.
 */
const SearchFiltersBar = ({
  searchInputProps = {},
  filters = [],
  actions = null,
  contentJustify = 'between',
  searchWidthClassName = '',
  showFiltersAction = false,
  onFiltersClick,
  filtersActionLabel = 'Filters',
  filtersActionLeftIcon = DefaultFiltersIcon,
  className = '',
  rowClassName = 'border-b border-edge pb-3',
}) => {
  const hasFilters = Array.isArray(filters) && filters.length > 0;
  const justifyClass = contentJustifyMap[contentJustify] ?? contentJustifyMap.between;
  const rightWrapperClass = useMemo(
    () =>
      contentJustify === 'between'
        ? 'flex min-w-0 flex-1 flex-wrap items-center justify-end gap-3'
        : 'flex min-w-0 flex-wrap items-center gap-3',
    [contentJustify],
  );

  const resolvedSearchInputProps = useMemo(
    () => ({
      ...searchInputProps,
      className: [
        searchWidthClassName || defaultSearchWidthClassName,
        defaultSearchInputClassName,
        searchInputProps.className,
      ]
        .filter(Boolean)
        .join(' '),
    }),
    [searchInputProps, searchWidthClassName],
  );

  const rowClassNameResolved = useMemo(
    () =>
      [
        'flex min-w-0 flex-wrap items-center gap-3',
        justifyClass,
        rowClassName,
      ]
        .filter(Boolean)
        .join(' '),
    [justifyClass, rowClassName],
  );

  return (
    <div className={['shrink-0', className].filter(Boolean).join(' ')}>
      <div className={rowClassNameResolved}>
        <SearchInput {...resolvedSearchInputProps} />

        {(hasFilters || actions || showFiltersAction) ? (
          <div className={rightWrapperClass}>
            {filters.map((filter) => (
              <div
                key={filter.id}
                className={filter.wrapperClassName || defaultSelectWrapperClassName}
                title={filter.title || filter.name || filter.placeholder}
              >
                {filter.multiple === false ? (
                  <FormSelect
                    id={filter.id}
                    value={typeof filter.value === 'string' ? filter.value : ''}
                    onChange={filter.onChange}
                    options={filter.options}
                    placeholder={filter.placeholder || filter.name}
                    showradioButton={Boolean(filter.showradioButton)}
                    align={filter.align}
                    triggerClassName={filter.triggerClassName || defaultTriggerClassName}
                    listboxClassName={filter.listboxClassName || defaultListboxClassName}
                  />
                ) : (
                  <FormMultiSelect
                    id={filter.id}
                    value={Array.isArray(filter.value) ? filter.value : []}
                    onChange={filter.onChange}
                    options={filter.options}
                    placeholder={filter.placeholder || filter.name}
                    renderValue={filter.renderValue}
                    align={filter.align}
                    triggerClassName={filter.triggerClassName || defaultTriggerClassName}
                    listboxClassName={filter.listboxClassName || defaultListboxClassName}
                  />
                )}
              </div>
            ))}
            {actions}
            {showFiltersAction ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                leftIcon={filtersActionLeftIcon || undefined}
                customButtonHeight="!h-8"
                customFontSize="text-xs"
                className={defaultFiltersButtonClassName}
                onClick={onFiltersClick}
              >
                {filtersActionLabel}
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default memo(SearchFiltersBar);
